import React, { FC } from "react";

import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Bold } from "@UI/Components/Bold/Bold";
import { ListItem } from "@UI/Components/List/List";
import { Text } from "@UI/Components/Text/Text";
import { Box } from "@UI/Components/Box/Box";
import { Rate } from "@Shared/Components/Rate/Rate";
import { IOrder } from "@modules/Purchasing/services/orders";

import messages from "../messages";
import { getDarkBorderColor } from "../config";
import { OrderDescriptionColorScheme } from "../types";

interface Props {
  order: IOrder;
  colorScheme?: OrderDescriptionColorScheme;
  preview?: boolean;
}

const VALUES_FOR_BILLING_UNIT = {
  hour: {
    cents: "itemsTotalHourCents",
    currency: "itemsTotalHourCurrency",
  },
  day: {
    cents: "itemsTotalDayCents",
    currency: "itemsTotalDayCurrency",
  },
  week: {
    cents: "itemsTotalWeekCents",
    currency: "itemsTotalWeekCurrency",
  },
};

const TOTAL_LABEL_FOR_BILLING_UNIT = {
  hour: messages.totalByHour,
  day: messages.totalByDay,
  week: messages.totalByWeek,
};

export const Total: FC<Props> = ({ order, colorScheme, preview }) => {
  const borderColor = getDarkBorderColor(colorScheme);

  const totalByBillingUnitUsed =
    order.itemsTotalHourCents > 0
      ? "hour"
      : order.itemsTotalDayCents > 0
      ? "day"
      : order.itemsTotalWeekCents > 0
      ? "week"
      : null;

  const getCentsByBillingUnit = (
    billingUnit: keyof typeof VALUES_FOR_BILLING_UNIT
  ) => order[VALUES_FOR_BILLING_UNIT[billingUnit].cents];

  const getCurrencyByBillingUnit = (
    billingUnit: keyof typeof VALUES_FOR_BILLING_UNIT
  ) => order[VALUES_FOR_BILLING_UNIT[billingUnit].currency];

  return (
    <ListItem
      display="flex"
      justifyContent="space-between"
      borderTopColor={borderColor}
      borderTopWidth="1px"
      pt="5"
    >
      <Bold fontSize="lg">
        {totalByBillingUnitUsed && preview ? (
          <FormattedMessage
            {...TOTAL_LABEL_FOR_BILLING_UNIT[totalByBillingUnitUsed]}
          />
        ) : (
          <FormattedMessage {...messages.total} />
        )}
      </Bold>
      <Box textAlign="right">
        {totalByBillingUnitUsed && preview ? (
          <>
            <Box pb="1">
              <Bold fontSize={{ base: "lg", lg: "xl" }}>
                <Rate
                  cents={getCentsByBillingUnit(totalByBillingUnitUsed)}
                  currency={getCurrencyByBillingUnit(totalByBillingUnitUsed)}
                  unit={totalByBillingUnitUsed}
                />
              </Bold>
            </Box>

            {order.itemsTotalFlatCents > 0 && (
              <Text fontSize="md">
                (+{" "}
                <Rate
                  cents={order.itemsTotalFlatCents}
                  currency={order.itemsTotalFlatCurrency}
                  unit="flat"
                />
                )
              </Text>
            )}
          </>
        ) : (
          <Bold fontSize={{ base: "lg", lg: "xl" }}>
            <Rate
              cents={order.itemsTotalCents}
              currency={order.itemsTotalCurrency}
              unit="flat"
            />
          </Bold>
        )}
      </Box>
    </ListItem>
  );
};
